"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

import { PdfFormInput } from "@/components/ui/pdf-form-input";
import { usePdfFileCache } from "@/common/hooks/use-pdf-file-cache";
import { CareFormStatusBadge } from "@/care-forms/components/care-forms/status-badge";
import { SignatureInput } from "@/components/ui/signature-input";

import { useNavigate, useParams } from "react-router";
import { useOs } from "@mantine/hooks";

import {
	Box,
	Button,
	Card,
	Container,
	Flex,
	Group,
	Loader,
	Modal,
	Stack,
	Text,
	TextInput,
	Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";

import { caregiverAppClientSignatureDialogDescription } from "@/caregiver-app/components/constants";
import {
	useGetCareSheetPDFDetails,
	useGetCareSheetPDFFile,
	useSaveCareSheetReview,
} from "@/caregiver-app/components/hooks";

import { navigateBack } from "@/common/utils/navigateBack";
import { ROUTES } from "@/setup/frontend/routes";
import { CareFormStatus } from "@/care-forms/models";

import { IconAlertCircle, IconArrowLeft } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "@/common/utils/trpc";

const emptyObject = {};
export default function CareSheetReviewPage() {
	const { id } = useParams<{ id: string }>();
	const careSheetId = Number(id);
	const navigate = useNavigate();
	const os = useOs();
	const isAndroid = os === "android";
	const {
		data: pdfDetails,
		isLoading,
		error,
	} = useGetCareSheetPDFDetails(careSheetId);
	const { data: pdfFile } = useGetCareSheetPDFFile(careSheetId);
	const { mutate: saveReview, isPending, isSuccess } = useSaveCareSheetReview();
	const queryClient = useQueryClient();

	const [fieldData, setFieldData] = useState<Record<string, any>>({});
	const [showBackConfirmation, setShowBackConfirmation] = useState(false);
	const [useFallbackMode, setUseFallbackMode] = useState(false);
	const [clientSignature, setClientSignature] = useState<string | null>(null);
	const [caregiverSignature, setCaregiverSignature] = useState<string | null>(
		null,
	);
	const [inlinePdfUrl, setInlinePdfUrl] = useState<string | null>(null);
	const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
	const [caregiverPrintedName, setCaregiverPrintedName] = useState<string>("");

	const generatePdfMutation =
		trpc.careSheets.generateAndDownloadPDF.useMutation({
			onSuccess: (data) => {
				setInlinePdfUrl(data.dataUrl);
				setIsGeneratingPdf(false);
			},
			onError: (error) => {
				notifications.show({
					title: "Error",
					message: error.message || "Failed to generate PDF",
					color: "red",
				});
				setIsGeneratingPdf(false);
			},
		});

	const handleGeneratePdf = useCallback(() => {
		setIsGeneratingPdf(true);
		generatePdfMutation.mutate({ care_sheet_id: careSheetId });
	}, [careSheetId, generatePdfMutation]);

	const { file: cachedFile, isLoading: pdfLoading } = usePdfFileCache({
		downloadUrl: pdfFile?.downloadUrl,
		uploadedAt: pdfFile?.pdf_uploaded_at,
		idPrefix: `care-sheet-${careSheetId}`,
	});

	const requiredFieldNames = useMemo(() => {
		return [
			...(pdfDetails?.finalCaregiverRequiredFieldNames || []),
			...(pdfDetails?.finalClientRequiredFieldNames || []),
		];
	}, [pdfDetails]);

	const finalSignatureFieldNames = useMemo(() => {
		return [
			...(pdfDetails?.finalCaregiverSignatureFieldNames || []),
			...(pdfDetails?.finalClientSignatureFieldNames || []),
		];
	}, [pdfDetails]);

	const caregiverNameField = useMemo(() => {
		if (!pdfDetails?.finalCaregiverRequiredFieldNames) return null;

		const nameField = pdfDetails.finalCaregiverRequiredFieldNames.find((name) =>
			name.endsWith("Name"),
		);
		if (nameField) {
			return nameField;
		}
		return null;
	}, [pdfDetails?.finalCaregiverRequiredFieldNames]);

	const onChange = useCallback(
		(e: Record<string, any>) => {
			setFieldData((s) => {
				const filteredEntries = Object.entries(e).filter(
					([key]) =>
						requiredFieldNames.includes(key) ||
						finalSignatureFieldNames.includes(key),
				);

				const newData = {
					...s,
					...Object.fromEntries(filteredEntries),
				};

				// Add signature date fields if any signatures were added
				for (const [key, value] of filteredEntries) {
					if (value && s[key] !== value) {
						if (pdfDetails?.finalCaregiverSignatureFieldNames.includes(key)) {
							Object.assign(
								newData,
								pdfDetails.finalCaregiverSignatureDateFieldData,
							);
						}
						if (pdfDetails?.finalClientSignatureFieldNames.includes(key)) {
							Object.assign(
								newData,
								pdfDetails.finalClientSignatureDateFieldData,
							);
						}
					}
				}

				return newData;
			});
		},
		[
			requiredFieldNames,
			finalSignatureFieldNames,
			pdfDetails?.finalCaregiverSignatureDateFieldData,
			pdfDetails?.finalClientSignatureDateFieldData,
			pdfDetails?.finalCaregiverSignatureFieldNames,
			pdfDetails?.finalClientSignatureFieldNames,
		],
	);

	const handleUseFallbackMode = useCallback(() => {
		setUseFallbackMode(true);
		// Generate the PDF for inline viewing
		handleGeneratePdf();
	}, [handleGeneratePdf]);

	useEffect(() => {
		if (pdfDetails) {
			setFieldData(pdfDetails.field_data || {});
		}
	}, [pdfDetails]);

	const allRequiredFieldsCompleted = useMemo(() => {
		if (useFallbackMode) {
			// In fallback mode, we need signatures from both client and caregiver
			if (
				pdfDetails?.finalCaregiverSignatureFieldNames.length &&
				!caregiverSignature
			) {
				return false;
			}
			if (
				pdfDetails?.finalClientSignatureFieldNames.length &&
				!clientSignature
			) {
				return false;
			}
			// Check printed name field is filled if required
			if (caregiverNameField && !caregiverPrintedName) {
				return false;
			}
			return true;
		}
		return requiredFieldNames.every((name) => fieldData[name]);
	}, [
		fieldData,
		requiredFieldNames,
		useFallbackMode,
		clientSignature,
		caregiverSignature,
		pdfDetails,
		caregiverNameField,
		caregiverPrintedName,
	]);

	const handleBack = () => {
		if (
			pdfDetails?.careSheet.status === CareFormStatus.clientReview ||
			pdfDetails?.careSheet.status === CareFormStatus.caregiverReview
		) {
			setShowBackConfirmation(true);
		} else {
			navigateBack(navigate, ROUTES.CAREGIVER_APP.ROOT);
		}
	};

	const handleConfirmBack = () => {
		setShowBackConfirmation(false);
		navigateBack(navigate, ROUTES.CAREGIVER_APP.ROOT);
	};

	const handleSubmit = () => {
		// If in fallback mode, combine the signatures with the existing field data
		const submitFieldData = { ...fieldData };

		if (useFallbackMode) {
			// Add caregiver signature if available
			if (
				caregiverSignature &&
				pdfDetails?.finalCaregiverSignatureFieldNames.length
			) {
				submitFieldData[pdfDetails.finalCaregiverSignatureFieldNames[0]] =
					caregiverSignature;
				// Add signature date fields
				if (pdfDetails.finalCaregiverSignatureDateFieldData) {
					Object.assign(
						submitFieldData,
						pdfDetails.finalCaregiverSignatureDateFieldData,
					);
				}
			}

			// Add client signature if available
			if (
				clientSignature &&
				pdfDetails?.finalClientSignatureFieldNames.length
			) {
				submitFieldData[pdfDetails.finalClientSignatureFieldNames[0]] =
					clientSignature;
				// Add signature date fields
				if (pdfDetails.finalClientSignatureDateFieldData) {
					Object.assign(
						submitFieldData,
						pdfDetails.finalClientSignatureDateFieldData,
					);
				}
			}

			// Add printed name field if needed
			if (caregiverNameField && caregiverPrintedName) {
				submitFieldData[caregiverNameField] = caregiverPrintedName;
			}
		}

		saveReview(
			{
				care_sheet_id: careSheetId,
				field_data: submitFieldData,
			},
			{
				onSuccess: () => {
					notifications.show({
						title: "Success",
						message: "Care Sheet Review Submitted",
						autoClose: 2000,
					});

					// Refresh all relevant data
					queryClient.invalidateQueries({
						queryKey: ["careSheets", "listMyCareSheets"],
					});
					queryClient.invalidateQueries({
						queryKey: [
							"careSheets",
							"getCareSheetPDFDetails",
							{ care_sheet_id: careSheetId },
						],
					});
					queryClient.invalidateQueries({
						queryKey: [
							"careSheets",
							"getCareSheetPDFFile",
							{ care_sheet_id: careSheetId },
						],
					});

					// If in fallback mode, refresh the generated PDF
					if (useFallbackMode) {
						handleGeneratePdf();
					}

					// Reset form state for signatures
					setCaregiverSignature(null);
					setClientSignature(null);
					setCaregiverPrintedName("");
				},
				onError: (error: any) => {
					notifications.show({
						title: "Error",
						message: error.message,
						color: "red",
					});
				},
			},
		);
	};

	if (isLoading) {
		return (
			<Flex direction="column" h="100vh">
				<Box style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}>
					<Group h={64} px="md" justify="space-between">
						<Title order={3}>Care Sheet Review</Title>
						<Box w={48} />
					</Group>
				</Box>
				<Flex align="center" justify="center" style={{ flex: 1 }}>
					<Stack align="center" gap="md">
						<Loader size="lg" color="blue" />
						<Text size="lg" c="dimmed" fw={500}>
							Loading details...
						</Text>
					</Stack>
				</Flex>
			</Flex>
		);
	}

	if (!pdfDetails || error) {
		return (
			<Container p="md">
				<Card withBorder>
					<Stack align="center" gap="md" p="md">
						<Text size="lg" fw={500}>
							Error loading review details
						</Text>
						{error && (
							<Text size="sm" c="red">
								{error.message}
							</Text>
						)}
						<Button onClick={() => navigate(ROUTES.CAREGIVER_APP.ROOT)}>
							Return to Home
						</Button>
					</Stack>
				</Card>
			</Container>
		);
	}

	return (
		<Flex
			direction="column"
			style={{ overflow: "hidden", width: "100%" }}
			py="md"
		>
			<Group h={64} px="md" justify="space-between" wrap="nowrap">
				<Button variant="subtle" onClick={handleBack}>
					<IconArrowLeft size={20} />
					<Text display="none">Back</Text>
				</Button>
				<Title order={4}>Care Sheet Review</Title>
				<CareFormStatusBadge status={pdfDetails.careSheet.status} />
			</Group>

			<Modal
				opened={showBackConfirmation}
				onClose={() => setShowBackConfirmation(false)}
				title="Confirm Navigation"
				centered
			>
				<Stack>
					<Text>
						This care sheet still needs to be reviewed and signed by the client.
						Are you sure you want to leave the page?
					</Text>
					<Group justify="flex-end">
						<Button
							variant="outline"
							onClick={() => setShowBackConfirmation(false)}
						>
							Stay on Page
						</Button>
						<Button color="red" onClick={handleConfirmBack}>
							Leave Page
						</Button>
					</Group>
				</Stack>
			</Modal>

			{!useFallbackMode ? (
				<>
					<Flex p="md" style={{ overflow: "auto" }}>
						<PdfFormInput
							documentKey={`care-sheet-${careSheetId}`}
							file={pdfLoading ? undefined : cachedFile}
							value={fieldData || emptyObject}
							onChange={onChange}
							signatureFieldNames={pdfDetails.signatureFieldNames}
							readOnlySignatureFieldNames={pdfDetails.signatureFieldNames.filter(
								(name: string) => !finalSignatureFieldNames.includes(name),
							)}
							requiredFieldNames={requiredFieldNames}
							readOnly={!pdfDetails.canSaveReview}
							defaultScale={1.2}
							signatureInputDialogDescription={
								caregiverAppClientSignatureDialogDescription
							}
							disableCircleMode={true}
						/>
					</Flex>

					<Box p="md">
						<Stack gap="lg">
							<Button
								leftSection={<IconAlertCircle size={16} />}
								color="orange"
								variant="outline"
								onClick={handleUseFallbackMode}
								disabled={!pdfFile?.downloadUrl || isAndroid}
								style={{ display: isAndroid ? "none" : undefined }}
							>
								PDF isn't loading? Tap here for alternative view
							</Button>
							<Button
								onClick={handleSubmit}
								fullWidth
								disabled={
									isPending ||
									isLoading ||
									!pdfDetails.canSaveReview ||
									!allRequiredFieldsCompleted ||
									isSuccess
								}
							>
								{isPending ? (
									<Loader size="sm" />
								) : pdfDetails.canSaveReview ? (
									"Save Review"
								) : (
									"Save Not Allowed"
								)}
							</Button>
						</Stack>
					</Box>
				</>
			) : (
				<Flex
					direction="column"
					p="md"
					gap="md"
					flex={1}
					style={{ overflow: "auto" }}
				>
					{isGeneratingPdf ? (
						<Flex
							align="center"
							justify="center"
							direction="column"
							gap="md"
							py="xl"
						>
							<Loader size="md" />
							<Text c="dimmed">Generating PDF...</Text>
						</Flex>
					) : inlinePdfUrl ? (
						<Flex flex={1} mih="50%">
							<iframe
								src={inlinePdfUrl}
								style={{ width: "100%", height: "100%", border: "none" }}
								title="Care Sheet PDF"
							/>
						</Flex>
					) : (
						<Stack align="center" gap="md">
							<Text c="dimmed">
								Failed to generate the PDF. Please refresh the page.
							</Text>
						</Stack>
					)}

					{/* Caregiver printed name input for fallback mode */}
					{caregiverNameField && pdfDetails.canSaveReview && (
						<Stack my="sm">
							<Text fw={500}>Caregiver Printed Name:</Text>
							<TextInput
								value={caregiverPrintedName}
								onChange={(e) => setCaregiverPrintedName(e.target.value)}
								placeholder="Enter your full name"
								required
								disabled={!pdfDetails.canSaveReview || isSuccess}
							/>
						</Stack>
					)}

					{/* Signature inputs for fallback mode */}
					{pdfDetails.finalCaregiverSignatureFieldNames.length > 0 &&
						pdfDetails.canSaveReview && (
							<Stack>
								<Text fw={500}>Caregiver Signature:</Text>
								<SignatureInput
									value={caregiverSignature}
									onChange={setCaregiverSignature}
									label="Caregiver Signature"
									readOnly={!pdfDetails.canSaveReview}
									required={true}
									dialogDescription={
										caregiverAppClientSignatureDialogDescription
									}
									signerName="Caregiver"
								/>
							</Stack>
						)}

					{pdfDetails.finalClientSignatureFieldNames.length > 0 &&
						pdfDetails.canSaveReview && (
							<Stack>
								<Text fw={500}>Client Signature:</Text>
								<SignatureInput
									value={clientSignature}
									onChange={setClientSignature}
									label="Client Signature"
									readOnly={!pdfDetails.canSaveReview}
									required={true}
									dialogDescription={
										caregiverAppClientSignatureDialogDescription
									}
									signerName="Client"
								/>
							</Stack>
						)}

					<Group grow mt="md">
						<Button variant="outline" onClick={() => setUseFallbackMode(false)}>
							Return to standard view
						</Button>
						<Button
							onClick={handleSubmit}
							disabled={
								isPending ||
								isLoading ||
								!pdfDetails.canSaveReview ||
								!allRequiredFieldsCompleted ||
								isSuccess
							}
						>
							{isPending ? (
								<Loader size="sm" />
							) : pdfDetails.canSaveReview ? (
								"Save Review"
							) : (
								"Save Not Allowed"
							)}
						</Button>
					</Group>
				</Flex>
			)}
		</Flex>
	);
}
